/**
 * Connects game signals to SFX playback for FishPrawnCrab. Audio is a passive
 * observer of Rive events / GameState changes.
 *
 * Chip-select, place-bet, start-bet and no-more-bet are GameState-driven (see
 * main.ts's `state.on(...)` chokepoint). Win/lose and the settlement chip
 * collection are orchestrator-driven (GameOrchestrator plays them directly via
 * the injected SoundManager). The dice-bowl cues ARE Rive events emitted by
 * FishPrawnCrab.riv — they're mapped in `EVENT_SFX` below and wired by
 * `bindGameSounds`.
 *
 * Finding the right Rive event names: every Rive event is logged by RiveLayer as
 * `[Rive][event:passthrough] <target> <name> <props>`. Map from those.
 */
import type { RiveLayer } from '../rive/riveLayer';
import type { SoundManager } from './soundManager';

// Rive event name → SFX clip base name. All four are emitted by FishPrawnCrab.riv
// (topBowlAnimation artboard). `Move` plays MoveBowl.mp3; the rest play their
// like-named .mp3. (Confirm names via RiveLayer's [Rive][event:passthrough] log.)
const EVENT_SFX: Readonly<Record<string, string>> = {
  Move: 'MoveBowl', // bowl slides in
  OpenBowl: 'OpenBowl', // bowl lifts to reveal the dice
  ShakeBowl: 'ShakeBowl', // bowl shaking
  CloseBowl: 'CloseBowl', // bowl closes over the dice
};

// State-driven clips, played from the GameState chokepoint in main.ts.
export const CHIP_SELECT_SFX = 'ClickBetSelect'; // chip select + place bet
export const START_BET_SFX = 'StartBet'; // betting opens (start-bet panel shown)
export const NO_MORE_BET_SFX = 'NoMoreBet'; // betting closes (no-more-bet panel shown)

// Orchestrator-driven clips, played from GameOrchestrator.
export const COLLECT_CHIPS_SFX = 'CollectChips'; // settlement chip distribution
export const WIN_SFX = 'YouWin'; // win panel shown
export const LOSE_SFX = 'YouLose'; // lose panel shown

// Distinct clips referenced anywhere — feed to SoundManager.preload().
export const SFX_CLIPS: readonly string[] = Array.from(
  new Set([
    ...Object.values(EVENT_SFX),
    CHIP_SELECT_SFX,
    START_BET_SFX,
    NO_MORE_BET_SFX,
    COLLECT_CHIPS_SFX,
    WIN_SFX,
    LOSE_SFX,
  ]),
);

/** Subscribe SFX playback to the relevant Rive events. */
export function bindGameSounds(rive: RiveLayer, sound: SoundManager): void {
  for (const [event, clip] of Object.entries(EVENT_SFX)) {
    rive.on(event, () => sound.play(clip));
  }
}
